import { useState } from "react";
import { formatPlanName } from "@utils/data-display";
import { AdmPlan, SubjectTypeProps } from "@custom-types/base-types";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";
import { useAppDispatch } from "@store/store-helper";
import { updatePlan } from "@store/plans/plans-thunks";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { Dialog, TextField, useToast, Text } from "@flat-ui";
import { getDeactivationComment } from "@utils/plan-actions-utils";

interface Props extends SubjectTypeProps {
  /** The plan that is about to get deactivated */
  plan: AdmPlan;

  /** Function called when the user closes the deactivation dialog */
  onClose(): void;
}

/**
 * Dialog to show when the user deactivates a plan. *
 * User has to enter a reason why the plan is being deactivated, and
 * the user is allowed to edit the plan's comment
 */
export function DeactivatePlanDialog({
  plan,
  onClose,
  subjectType,
}: Props): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [planComment, setPlanComment] = useState<string>(
    plan.description ?? ""
  );
  const [deactivationComment, setDeactivationComment] = useState<string>("");

  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandler();
  const { openToast } = useToast();

  async function handleConfirm(): Promise<void> {
    if (subjectType === "DEVICE") {
      trackEvent({
        name: PlanEvents.deactivateLicense,
        props: {
          licenseId: plan.id,
          licenseName: formatPlanName(plan),
        },
      });
    } else {
      trackEvent({
        name: PlanEvents.deactivatePlan,
        props: {
          target: subjectType,
          planId: plan.id,
          planName: formatPlanName(plan),
        },
      });
    }

    try {
      setIsFetching(true);

      await dispatch(
        updatePlan({
          planId: plan.id,
          updatedProps: {
            type: "deactivating",
            deactivatedComment: getDeactivationComment({
              planComment,
              reason: deactivationComment,
            }),
          },
        })
      ).unwrap();

      openToast({
        title: "The selected plan is deactivated successfully!",
      });
      onClose();
    } catch (error) {
      handleErrorWithToast({
        id: `deactivating-plan-${Date.now().toString()}`,
        title: "Could not deactivate plan",
        error,
      });
    } finally {
      setIsFetching(false);
    }
  }

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      title={`deactivate plan "${formatPlanName(plan)}"`}
      onConfirm={handleConfirm}
      showSpinner={isFetching}
      confirmText="Yes, deactivate"
      isConfirmDisabled={!deactivationComment}
      confirmTooltip="A deactivation reason must be entered"
    >
      <Text variant="bodyL">
        Are you sure you want to deactivate this plan?
      </Text>
      <TextField
        label="Current plan comments"
        multiline
        minRows={2}
        text={planComment}
        onTextChanged={setPlanComment}
        variant="outlined"
        fullWidth
        controlStyle={{ mt: 2 }}
      />

      <TextField
        label="Deactivation reason"
        multiline
        placeholder="Please enter the reason why this plan is being deactivated"
        minRows={2}
        text={deactivationComment}
        onTextChanged={setDeactivationComment}
        variant="outlined"
        fullWidth
        tag="required"
        controlStyle={{ mt: 2 }}
      />
    </Dialog>
  );
}
